import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryFn } from "../../lib/apiInstance";
import { LOCAL_STORAGE_KEY } from "../../lib/constant/constant";
import { BASE_URL, EndpointSlug } from "../../lib/constant/endPoints";

/**
 * apiService - Redux Toolkit Query API Service
 * ------------------------------------------------------------
 * Centralized API service for user authentication-related endpoints.
 * Provides RTK Query hooks for performing mutations (login, password management).
 *
 * Features:
 * - Automatic Authorization header with token from localStorage
 * - Custom base query with timeout and error handling
 * - Easy extension for additional endpoints
 */
export const apiService = createApi({
  reducerPath: "apiService",

  /** Base query configuration using enhanced baseQueryFn */
  baseQuery: baseQueryFn({
    baseUrl: BASE_URL,
    prepareHeaders: (headers) => {
      const token =
        localStorage.getItem(LOCAL_STORAGE_KEY.AUTH_TOKEN) ||
        sessionStorage.getItem(LOCAL_STORAGE_KEY.AUTH_TOKEN) ||
        "";
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),

  /** Tag types for cache invalidation (optional, currently empty) */
  tagTypes: [
    "Campaigns",
    "Experience",
    "Users",
    "SimulationConfigurations",
    "SimulationTemplates",
    "Tenants",
    "TenantsById",
    "applicationOnboarding",
  ],

  /** API endpoints definition */
  endpoints: (builder) => ({
    /**
     * Login mutation
     * @param {Object} credentials - { email, password }
     */
    login: builder.mutation({
      query: (credentials) => ({
        url: `${EndpointSlug.USER_AUTH}`,
        method: "POST",
        body: { ...credentials },
        headers: { "Content-Type": "application/json" },
      }),
    }),

    /**
     * Change password mutation
     * @param {Object} credentials - { oldPassword, newPassword }
     */
    changePassword: builder.mutation({
      query: (credentials) => ({
        url: `${EndpointSlug.USER_CHANGE_PASSWORD}`,
        method: "POST",
        body: credentials,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    /**
     * Change admin password mutation
     * @param {Object} credentials - { oldPassword, newPassword }
     */
    changeAdminPassword: builder.mutation({
      query: (credentials) => ({
        url: `${EndpointSlug.ADMIN_CHANGE_PASSWORD}`,
        method: "POST",
        body: credentials,
        headers: { "Content-Type": "application/json" },
      }),
    }),

    /**
     * Forgot password mutation
     * @param {Object} credentials - { email }
     */
    forgotPassword: builder.mutation({
      query: (credentials) => ({
        url: `${EndpointSlug.USER_FORGOT_PASSWORD}`,
        method: "POST",
        body: credentials,
        headers: { "Content-Type": "application/json" },
      }),
    }),

    /**
     * Reset password mutation
     * @param {Object} credentials - { token, newPassword }
     */
    resetPassword: builder.mutation({
      query: (credentials) => ({
        url: `${EndpointSlug.USER_RESET_PASSWORD}`,
        method: "POST",
        body: credentials,
        headers: { "Content-Type": "application/json" },
      }),
    }),
    // Get-Presigned URL
    getPresignedURL: builder.query({
      query: ({ contentType = "", fileName = "", tenantId = "" }) => ({
        url: `${EndpointSlug.GET_PRESIGNED_URL}/${tenantId}/branding/presign`,
        method: "GET",
        params: {
          contentType,
          fileName,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),

    getCampaigns: builder.query({
      query: () => ({
        url: `${EndpointSlug.GET_CAMPAIGN}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["Campaigns"],
    }),

    getCampaignsByTenantId: builder.query({
      query: ({ tenantId }) => ({
        url: `${EndpointSlug.GET_CAMPAIGN_BY_TENANTID}/${tenantId}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["Campaigns"],
    }),

    createCampaign: builder.mutation({
      query: (body) => ({
        url: `${EndpointSlug.CREATE_CAMPAIGN}`,
        method: "POST",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Campaigns"],
    }),

    getCampaignById: builder.query({
      query: ({ campaignId }) => ({
        url: `${EndpointSlug.GET_CAMPAIGN}/${campaignId}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["Campaigns"],
    }),

    updateCampaign: builder.mutation({
      query: ({ body, campaignId }) => ({
        url: `${EndpointSlug.UPDATE_CAMPAIGN}/${campaignId}`,
        method: "PATCH",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Campaigns"],
    }),

    createExperience: builder.mutation({
      query: (body) => ({
        url: `${EndpointSlug.CREATE_EXPERIENCE}`,
        method: "POST",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Experience"],
    }),

    updateExperience: builder.mutation({
      query: ({ body }) => ({
        url: `${EndpointSlug.UPDATE_EXPERIENCE}`,
        method: "PATCH",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Experience"],
    }),
    getSimulationTemplates: builder.query({
      query: () => ({
        url: `${EndpointSlug.GET_SIMULATION_TEMPLATES}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["SimulationTemplates"],
    }),
    /**
     * Create tenant
     * @param {Object}
     */
    createTenant: builder.mutation({
      query: (payload) => ({
        url: `${EndpointSlug.CREATE_TENANT}`,
        method: "POST",
        body: payload,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["Tenants"],
    }),
    /**
     * Get Tenants list
     * @param {Object}
     */
    getTenants: builder.query({
      query: () => ({
        url: `${EndpointSlug.GET_TENANT}`,
        method: "GET",
      }),
      providesTags: ["Tenants"],
    }),
    updateTenantBranding: builder.mutation({
      query: ({ tenantId, payload }) => ({
        url: `${EndpointSlug.UPDATE_TENANT_BRANDING}/${tenantId}/branding`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["TenantsById"],
    }),
    /**
     * Get Tenants by Id
     * @param {Object}
     */
    getTenantById: builder.query({
      query: (tenantId) => ({
        url: `${EndpointSlug.GET_TENANT_BY_ID}/${tenantId}`,
        method: "GET",
      }),
      providesTags: ["TenantsById"],
    }),
    updateTenant: builder.mutation({
      query: ({ tenantId, payload }) => ({
        url: `${EndpointSlug.UPDATE_TENANT}/${tenantId}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["Tenants", "TenantsById"],
    }),
    deactivateTenant: builder.mutation({
      query: (tenantId) => ({
        url: `${EndpointSlug.DEACTIVATE_TENANT_BY_ID}/${tenantId}/deactivate`,
        method: "PATCH",
        body: {},
      }),
      invalidatesTags: ["Tenants"],
    }),

    getSimulationConfigurations: builder.query({
      query: () => ({
        url: `${EndpointSlug.GET_SIMULATION_CONFIGURATIONS}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["SimulationConfigurations"],
    }),

    getSimConfigByExperienceId: builder.query({
      query: ({ experienceId }) => ({
        url: `${EndpointSlug.GET_SIMULATION_CONFIGURATIONS}`,
        method: "GET",
        params: {
          experienceId,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["SimulationConfigurations"],
    }),

    getSimulationConfigurationsById: builder.query({
      query: ({ configurationId }) => ({
        url: `${EndpointSlug.GET_SIMULATION_CONFIGURATIONS}/${configurationId}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["SimulationConfigurations"],
    }),
    adminForgotPassword: builder.mutation({
      query: (credentials) => ({
        url: `${EndpointSlug.FORGOT_PASSWORD}`,
        method: "POST",
        body: credentials,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),

    getExperienceUsers: builder.query({
      query: ({ experienceId }) => ({
        url: `${EndpointSlug.GET_EXPERIENCE_USERS}/${experienceId}/users`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["Experience"],
    }),

    createSimulationConfiguration: builder.mutation({
      query: (body) => ({
        url: `${EndpointSlug.CREATE_SIMULATION_CONFIGURATIONS}`,
        method: "POST",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["SimulationConfigurations"],
    }),

    updateSimulationConfiguration: builder.mutation({
      query: ({ body, configurationId }) => ({
        url: `${EndpointSlug.UPDATE_SIMULATION_CONFIGURATIONS}/${configurationId}`,
        method: "PATCH",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["SimulationConfigurations"],
    }),

    publishSimulationConfigurations: builder.mutation({
      query: ({ configurationId, body = {} }) => ({
        url: `${EndpointSlug.PUBLISH_SIMULATION_CONFIGURATIONS}/${configurationId}/publish`,
        method: "PATCH",
        body,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
      // invalidatesTags: ["SimulationConfigurations"],
    }),

    getExperienceByCampaign: builder.query({
      query: ({ campaignId }) => ({
        url: `${EndpointSlug.GET_EXPERIENCES_BY_CAMPAIGN}`,
        method: "GET",
        params: {
          campaignId,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["Experience"],
    }),
    getOverallAnalytics: builder.query({
      query: () => ({
        url: `${EndpointSlug.OVERALL_ANALYTICS}`,
        method: "GET",
      }),
    }),
    getTenantAnalytics: builder.query({
      query: ({ tenantId, campaignId, days = 30 }) => ({
        url: `${EndpointSlug.TENANT_ANALYTICS}/${tenantId}?campaignId=${campaignId}&days=${days}`,
        method: "GET",
      }),
    }),
    getExperienceParticipationAnalytics: builder.query({
      query: (experienceId) => ({
        url: `${EndpointSlug.Experience_Participation}/${experienceId}/participation`,
        method: "GET",
      }),
    }),
    getTenantCampaigns: builder.query({
      query: (tenantId) => ({
        url: `${EndpointSlug.TENANT_CAMPAIGNS}/${tenantId}`,
        method: "GET",
      }),
    }),

    publishExperience: builder.mutation({
      query: ({ experienceId, body }) => ({
        url: `${EndpointSlug.PUBLISH_EXPERIENCES}/${experienceId}/publish`,
        method: "PATCH",
        body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Experience"],
    }),

    getSimulationConfigPresignedURL: builder.query({
      query: ({ contentType = "", fileName = "" } = {}) => ({
        url: `${EndpointSlug.SIMULATION_CONFIGURATIONS_ASSETS_PRESIGN}`,
        method: "GET",
        params: {
          contentType,
          fileName,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["SimulationConfigurations"],
    }),

    // create Application Onboarding
    createAppOnboarding: builder.mutation({
      query: (body) => ({
        url: `${EndpointSlug.CREATE_APPLICATION_ONBOARDING}`,
        method: "POST",
        body: body,
        headers: {
          "Content-type": "application/json",
        },
      }),
      invalidatesTags: ["applicationOnboarding"],
    }),

    getAppOnboardingByTenantId: builder.query({
      query: ({ tenantId }) => ({
        url: `${EndpointSlug.GET_APPLICATION_ONBOARDING}`,
        method: "GET",
        params:{tenantId},
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["applicationOnboarding"],
    }),

    getAppOnboardingById: builder.query({
      query: ({ onboardingId }) => ({
        url: `${EndpointSlug.GET_APPLICATION_ONBOARDING_BY_ID}/${onboardingId}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["applicationOnboarding"],
    }),

    updateAppOnboarding: builder.mutation({
      query: ({ body, onboardingId }) => ({
        url: `${EndpointSlug.UPDATE_APPLICATION_ONBOARDING}/${onboardingId}`,
        method: "PATCH",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["applicationOnboarding"],
    }),
    // TODO: Add more endpoints as needed
  }),
});

/**
 * Export RTK Query hooks for each defined mutation
 */
export const {
  useLoginMutation,
  useChangePasswordMutation,
  useChangeAdminPasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetPresignedURLQuery,
  useGetCampaignsQuery,
  useCreateCampaignMutation,
  useGetCampaignByIdQuery,
  useUpdateCampaignMutation,
  useCreateExperienceMutation,
  useUpdateExperienceMutation,
  usePublishExperienceMutation,
  useGetSimulationTemplatesQuery,
  useCreateTenantMutation,
  useGetTenantsQuery,
  useGetTenantByIdQuery,
  useDeactivateTenantMutation,
  useAdminForgotPasswordMutation,
  useUpdateTenantMutation,
  useUpdateTenantBrandingMutation,
  useGetSimulationConfigurationsQuery,
  useGetSimulationConfigurationsByIdQuery,
  useGetExperienceUsersQuery,
  useCreateSimulationConfigurationMutation,
  useUpdateSimulationConfigurationMutation,
  usePublishSimulationConfigurationsMutation,
  useGetCampaignsByTenantIdQuery,
  useLazyGetPresignedURLQuery,
  useGetExperienceByCampaignQuery,
  useGetOverallAnalyticsQuery,
  useGetTenantAnalyticsQuery,
  useGetExperienceParticipationAnalyticsQuery,
  useGetTenantCampaignsQuery,
  useGetSimConfigByExperienceIdQuery,
  // useGetSimulationConfigPresignedURLQuery,
  useLazyGetSimulationConfigPresignedURLQuery,

  // Application onboarding
  useCreateAppOnboardingMutation,
  useGetAppOnboardingByIdQuery,
  useGetAppOnboardingByTenantIdQuery,
  useUpdateAppOnboardingMutation,

  // TODO: Export additional hooks here
} = apiService;
